/**
 * 지금 영업 중인지, 언제 닫고 언제 여는지 한 줄로 만든다.
 *
 * 왜 필요한가 — 네이버 플레이스·Fresha·마메드네 같은 이 카테고리의 예약 화면은 전부
 * 상단에 「영업 중 / 오후 8시 마감」을 띄운다. 우리는 주간 영업시간을 이미 공개 응답으로
 * 내려받으면서도 화면에 쓰지 않았다(실측 2026-08-11 경쟁 대조에서 미커버로 남아 있었다).
 * 지금 갈 수 있는지가 예약 결정의 첫 질문인데 그 답이 화면에 없었다.
 *
 * 요일 키는 서버가 0(일)~6(토) 문자열로 준다. 값이 없으면 그날은 쉰다.
 */
export function openStateOf(
  weeklySchedule: Record<string, [string, string]> | null | undefined,
  now = new Date(),
): { open: boolean; label: string } | null {
  if (!weeklySchedule || Object.keys(weeklySchedule).length === 0) return null;
  const partsOf = (value: string): { hour: number; minute: number } | null => {
    const [rawHour, rawMinute] = value.split(":");
    const hour = Number(rawHour);
    const minute = Number(rawMinute);
    return Number.isFinite(hour) && Number.isFinite(minute) ? { hour, minute } : null;
  };
  const minutesOf = (value: string) => {
    const parts = partsOf(value);
    return parts ? parts.hour * 60 + parts.minute : null;
  };
  const speak = (value: string) => {
    const parts = partsOf(value);
    if (!parts) return value;
    const meridiem = parts.hour < 12 ? "오전" : "오후";
    const display = parts.hour % 12 === 0 ? 12 : parts.hour % 12;
    return parts.minute ? `${meridiem} ${display}시 ${parts.minute}분` : `${meridiem} ${display}시`;
  };

  const today = weeklySchedule[String(now.getDay())];
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  if (today) {
    const start = minutesOf(today[0]);
    const end = minutesOf(today[1]);
    if (start !== null && end !== null) {
      if (nowMinutes >= start && nowMinutes < end) {
        return { open: true, label: `지금 영업 중 · ${speak(today[1])} 마감` };
      }
      if (nowMinutes < start) return { open: false, label: `오늘 ${speak(today[0])}에 열어요` };
    }
  }
  // 오늘은 끝났거나 쉬는 날 — 다음으로 여는 날을 찾는다. 없으면 아무 말도 하지 않는다.
  const names = ["일", "월", "화", "수", "목", "금", "토"];
  for (let step = 1; step <= 7; step += 1) {
    const index = (now.getDay() + step) % 7;
    const next = weeklySchedule[String(index)];
    if (!next) continue;
    const when = step === 1 ? "내일" : `${names[index]}요일`;
    return { open: false, label: `지금은 영업 시간이 아니에요 · ${when} ${speak(next[0])}에 열어요` };
  }
  return null;
}
